import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SuggestedContentItem {
  content_id: string;
  name: string;
  content_type: string | null;
  category: string | null;
  description: string | null;
  thumbnail_url: string | null;
  duration: string | null;
  reason: string;
  score: number;
}

export interface SuggestedTemplateItem {
  template_id: string;
  name: string;
  category: string | null;
  description: string | null;
  file_url: string | null;
  content: string | null;
  reason: string;
  score: number;
}

export interface ProjectSuggestionsRow {
  suggested_content: SuggestedContentItem[];
  suggested_templates: SuggestedTemplateItem[];
  generated_at: string | null;
}

export function useProjectSuggestions(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-suggestions', projectId],
    queryFn: async (): Promise<ProjectSuggestionsRow | null> => {
      if (!projectId) return null;
      const { data, error } = await supabase
        .from('project_suggestions')
        .select('suggested_content, suggested_templates, generated_at')
        .eq('project_id', projectId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        suggested_content: (data.suggested_content as any) || [],
        suggested_templates: (data.suggested_templates as any) || [],
        generated_at: data.generated_at,
      };
    },
    enabled: !!projectId,
  });
}

export function useGenerateProjectSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, force }: { projectId: string; force?: boolean }) => {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (sessionError || !accessToken) {
        throw new Error('Debes iniciar sesión para generar recomendaciones');
      }

      const { data, error } = await supabase.functions.invoke('recommend-project-resources', {
        body: { projectId, force: !!force },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['project-suggestions', projectId] });
    },
    onError: (err: any) => {
      console.error('Suggestion generation failed', err);
      toast.error(err?.message || 'No se pudieron generar sugerencias');
    },
  });
}
